<template>
  <div class="flex items-center justify-end gap-2 whitespace-nowrap">
    <button
      v-if="row.next_step"
      type="button"
      class="text-sm font-medium text-blue-600 hover:underline"
      data-cy="import-next-step"
      @click="emit('action', row.next_step)"
    >
      {{ ACTION_LABELS[row.next_step] }}
    </button>
    <!-- ActionsMenu renders into document.body, so the sticky Action cells below cannot cover it. -->
    <ActionsMenu
      v-if="menuEntries.length"
      data-cy="import-row-menu"
      :actions="menuEntries"
      v-on="listeners"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import ActionsMenu from "@/components/ActionsMenu.vue";
import {
  ACTION_LABELS,
  fileMenuLabel,
  type HubFile,
  type ImportAction,
  type ImportRow
} from "@/DataImports/importsHubRows";

const props = defineProps<{ row: ImportRow }>();
const emit = defineEmits<{
  action: [action: ImportAction, file?: HubFile];
}>();

interface MenuEntry {
  label: string;
  eventName: string;
  className?: string;
  run: () => void;
}

// ActionsMenu colours every item gray with an important class, so the red has to be important too.
const DANGER_CLASS = "!text-red-600";

function entriesFor(action: ImportAction): MenuEntry[] {
  if (action === "download_file") {
    const files = props.row.links?.files ?? [];
    return files.map((file) => ({
      label: fileMenuLabel(file, files.length),
      eventName: `download_file:${file.dataset}`,
      run: () => emit("action", action, file)
    }));
  }
  return [
    {
      label: ACTION_LABELS[action],
      eventName: action,
      className: action === "wipe" ? DANGER_CLASS : undefined,
      run: () => emit("action", action)
    }
  ];
}

// Wipe is destructive, so it sits last, and only on a row that carries its link.
const menuEntries = computed(() => {
  const { actions = [], links } = props.row;
  const wipe: ImportAction[] =
    actions.includes("wipe") && links?.wipe ? ["wipe"] : [];
  return [...actions.filter((action) => action !== "wipe"), ...wipe].flatMap(
    entriesFor
  );
});

const listeners = computed(() =>
  Object.fromEntries(
    menuEntries.value.map(({ eventName, run }) => [eventName, run])
  )
);
</script>
